import { Camera } from "@/lib/regions";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import { Button, Card, CardActions, Box, Select, Typography } from "@mui/material";
import React, { useRef } from "react";
import PlayerVideoContent from "./PlayerVideoContent";
import PlayerError from "./PlayerError";
import StyledMenuItem from "./StyledMenuItem";
import { useVideoPlayer } from "./hooks/useVideoPlayer";

interface PlayerProps {
  url: string;
  name: string;
  index: number;
  camera: Camera;
  availableCameras: Camera[];
  canDelete: boolean;
  onCameraChange: (camera: Camera) => void;
  onDelete: () => void;
}

const Player: React.FC<PlayerProps> = ({
  url,
  name,
  camera,
  availableCameras,
  canDelete,
  onCameraChange,
  onDelete,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isLink = camera.kind === "link";
  const hasStream = !isLink && url !== "";
  const { showError, stalled, cleanupVideo, retry } = useVideoPlayer(
    videoRef,
    hasStream ? url : "",
    name,
  );

  const handleDelete = () => {
    cleanupVideo();
    onDelete();
  };

  return (
    <Card
      sx={{
        display: "flex",
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        position: "relative",
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        "&:hover": {
          "& .player-delete": {
            backgroundColor: "red",
            transform: "translateX(0)",
            opacity: 1,
          },
        },
      }}
    >
      {hasStream ? (
        <PlayerVideoContent
          videoRef={videoRef}
          showError={showError}
          stalled={stalled}
          onDelete={handleDelete}
          canDelete={canDelete}
          onRetry={retry}
          cameraName={name}
        />
      ) : isLink ? (
        <Box
          sx={{
            width: "100%",
            minHeight: 180,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            p: 3,
            textAlign: "center",
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 28, color: "accent.main" }} />
          <Typography variant="h4" sx={{ color: "text.primary" }}>
            {name}
          </Typography>
          {camera.source && (
            <Typography variant="overline" sx={{ color: "text.secondary", mt: -0.25 }}>
              via {camera.source}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: "0.85rem",
              color: "text.secondary",
              maxWidth: 320,
              lineHeight: 1.45,
              mt: 0.5,
            }}
          >
            This camera is hosted externally — the live feed opens in a new tab.
          </Typography>
          <Button
            variant="contained"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            sx={{ mt: 1.5, textTransform: "none", fontWeight: 600, px: 2.5 }}
          >
            Open camera
          </Button>
        </Box>
      ) : (
        <PlayerError>
          <VideocamOffIcon sx={{ fontSize: 32, color: "text.secondary" }} />
          <Typography
            sx={{ fontSize: "0.85rem", color: "text.primary", fontWeight: 500 }}
          >
            {name}
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
            No stream available
          </Typography>
        </PlayerError>
      )}
      {availableCameras.length > 1 && (
        <CardActions sx={{ width: "100%", px: 1, pb: 1 }}>
          <Box sx={{ width: "100%" }}>
            <Select
              fullWidth
              size="small"
              value={camera.id}
              onChange={(e) => {
                const cam = availableCameras.find((c) => c.id === e.target.value);
                if (cam) onCameraChange(cam);
              }}
              sx={{ fontSize: "0.85rem" }}
            >
              {availableCameras.map((cam) => (
                <StyledMenuItem key={cam.id} value={cam.id}>
                  {cam.name}
                </StyledMenuItem>
              ))}
            </Select>
          </Box>
        </CardActions>
      )}
    </Card>
  );
};

export default Player;
